import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { firestore } from '../Firebase';
import { useParams } from 'react-router-dom';
import { serverTimestamp } from 'firebase/firestore';
import { AuthContextPublic } from '../context/AuthContextPublic';
import toast, { Toaster } from 'react-hot-toast';
import {
  buildQuestionPayload,
  getQuestionDisplayName,
  shouldShowQuestionManagementControls,
} from '../utils/questions';

const QuestionBox = () => {
  const [questionInput, setQuestionInput] = useState('');
  const [questions, setQuestions] = useState([]);
  const { videoID: paramVideoID } = useParams();
  const { userName } = useContext(AuthContextPublic) || {};
  const [shouldScroll, setShouldScroll] = useState(false);
  const questionsEndRef = useRef(null);
  const [showHiddenQuestions, setShowHiddenQuestions] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [isClearingQuestions, setIsClearingQuestions] = useState(false);
  const [features, setFeatures] = useState({ hasAnonymousQuestions: true });
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const showQuestionManagementControls =
    featuresLoaded && shouldShowQuestionManagementControls(features);

  useEffect(() => {
    const unsubscribe = firestore
      .collection('settings')
      .doc('features')
      .onSnapshot((doc) => {
        setFeatures(doc.exists ? doc.data() : {});
        setFeaturesLoaded(true);
      });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (shouldScroll) {
      scrollToBottom();
      setShouldScroll(false);
    }
  }, [questions, shouldScroll]);

  useEffect(() => {
    if (!showQuestionManagementControls) {
      setShowClearModal(false);
    }
  }, [showQuestionManagementControls]);

  const scrollToBottom = () => {
    const container = document.querySelector('.questions-window');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    const unsubscribe = firestore
      .collection('videos')
      .doc(paramVideoID)
      .collection('questions')
      .orderBy('timestamp', 'asc')
      .onSnapshot((querySnapshot) => {
        setQuestions(
          querySnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }))
        );
        setShouldScroll(true);
      });
    return () => unsubscribe();
  }, [paramVideoID]);

  const handleSendQuestion = async () => {
    if (!featuresLoaded || !questionInput.trim()) return;
    try {
      await firestore
        .collection('videos')
        .doc(paramVideoID)
        .collection('questions')
        .add(
          buildQuestionPayload({
            text: questionInput,
            userName,
            isAnonymous: features.hasAnonymousQuestions,
            timestamp: serverTimestamp(),
          })
        );

      toast.success(`Question submitted: ${questionInput}`);
      setQuestionInput('');
      setShouldScroll(true);
    } catch (error) {
      console.error('Error sending question:', error);
    }
  };

  const handleToggleVisibility = async (questionId, currentVisibility) => {
    try {
      await firestore
        .collection('videos')
        .doc(paramVideoID)
        .collection('questions')
        .doc(questionId)
        .set({
          visible: currentVisibility === 1 ? 0 : 1,
        }, { merge: true });
    } catch (error) {
      console.error('Error toggling question visibility:', error);
    }
  };

  const downloadCSV = () => {
    if (!featuresLoaded || !showQuestionManagementControls) return;

    const headers = ['date_submitted', 'name', 'question'];
    let csvContent = headers.join(',') + '\n';

    questions.forEach((question) => {
      let dateSubmitted = '';
      if (question.timestamp?.toDate) {
        dateSubmitted = question.timestamp.toDate().toLocaleString();
      }

      const row = [
        dateSubmitted,
        getQuestionDisplayName({
          name: question.name,
          isAnonymous: features.hasAnonymousQuestions,
        }),
        question.text || '',
      ];

      const escapedRow = row.map((field) => {
        const stringField = String(field);
        if (stringField.includes(',') || stringField.includes('"')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      });

      csvContent += escapedRow.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `questions_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearQuestions = async () => {
    if (!paramVideoID || !showQuestionManagementControls) return;

    setIsClearingQuestions(true);

    try {
      const questionsRef = firestore
        .collection('videos')
        .doc(paramVideoID)
        .collection('questions');
      let deletedCount = 0;
      let snapshot;

      do {
        snapshot = await questionsRef.limit(500).get();

        if (snapshot.empty) {
          break;
        }

        const batch = firestore.batch();
        snapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });

        await batch.commit();
        deletedCount += snapshot.size;
      } while (!snapshot.empty);

      setShowClearModal(false);
      toast.success(`Cleared ${deletedCount} question${deletedCount === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Error clearing questions:', error);
      toast.error('Error clearing questions: ' + error.message);
    } finally {
      setIsClearingQuestions(false);
    }
  };

  return (
    <>
      <Toaster />
      <div style={{ ...styles.header, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h5 style={{ margin: 0 }}>Questions</h5>
        {showQuestionManagementControls && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={downloadCSV}
              disabled={!featuresLoaded}
              style={{
                padding: '4px 8px',
                backgroundColor: featuresLoaded ? '#0d6efd' : '#adb5bd',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: featuresLoaded ? 'pointer' : 'not-allowed',
                fontSize: '13px',
              }}
            >
              Download CSV
            </button>
            <button
              onClick={() => setShowClearModal(true)}
              disabled={questions.length === 0 || isClearingQuestions}
              style={{
                padding: '4px 8px',
                backgroundColor: questions.length === 0 || isClearingQuestions ? '#adb5bd' : '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: questions.length === 0 || isClearingQuestions ? 'not-allowed' : 'pointer',
                fontSize: '13px',
              }}
            >
              Clear Questions
            </button>
          </div>
        )}
      </div>
      <Modal
        show={showQuestionManagementControls && showClearModal}
        onHide={() => !isClearingQuestions && setShowClearModal(false)}
        centered
      >
        <Modal.Header closeButton={!isClearingQuestions}>
          <Modal.Title>Clear questions?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will delete all submitted questions for this video/event.
          </p>
          <p className="mb-0">
            It only clears the <code>videos/{paramVideoID}/questions</code> records.
            It does not delete videos, registered users, login sessions, chats, or other event content.
            Download the CSV first if you need to keep a record.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowClearModal(false)}
            disabled={isClearingQuestions}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={clearQuestions}
            disabled={isClearingQuestions}
          >
            {isClearingQuestions ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Clearing...
              </>
            ) : (
              'Delete all questions'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <div className="input-area" style={styles.inputArea}>
        <div style={styles.inputWrapper}>
          <textarea
            placeholder="Type a question..."
            style={styles.input}
            disabled={!featuresLoaded}
            value={questionInput}
            onChange={(e) => setQuestionInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendQuestion();
              }
            }}
          />
          <button
            style={{
              ...styles.button,
              opacity: featuresLoaded ? 1 : 0.65,
              cursor: featuresLoaded ? 'pointer' : 'not-allowed',
            }}
            onClick={handleSendQuestion}
            disabled={!featuresLoaded}
          >
            Send
          </button>
        </div>
      </div>

      <div className="question-box-container" style={styles.container}>
        <div className="h6">Incoming Questions:</div>
        <div className="questions-window" style={styles.questionsWindow}>
          {questions
            .filter((question) => question.visible === 1)
            .map((question, index) => (
              <div
                key={index}
                style={{
                  ...styles.message,
                  backgroundColor: '#fff',
                }}
              >
                <div style={styles.messageContent}>
                  <span style={styles.messageName}>
                    {getQuestionDisplayName({
                      name: question.name,
                      isAnonymous: features.hasAnonymousQuestions,
                    })}
                    :{' '}
                  </span>
                  <span style={styles.messageText}>{question.text}</span>
                  <button
                    onClick={() => {
                      console.log('Toggling question:', question.id);
                      handleToggleVisibility(question.id, question.visible);
                    }}
                    style={styles.hideButton}
                  >
                    Hide
                  </button>
                </div>
                <div style={styles.messageTimestamp}>
                  {question.timestamp?.toDate().toLocaleTimeString()}
                </div>
              </div>
            ))}
          <div ref={questionsEndRef} />
        </div>
      </div>
      <div className="question-box-container" style={styles.container}>
        <div
          className="h6"
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => setShowHiddenQuestions(!showHiddenQuestions)}
        >
          <span style={{ marginRight: '8px' }}>
            {showHiddenQuestions ? '▼' : '▶'}
          </span>
          Hidden Questions
        </div>
        {showHiddenQuestions && (
          <div
            className="hidden-questions-window"
            style={styles.questionsWindow}
          >
            {questions
              .filter((question) => question.visible === 0)
              .map((question, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.message,
                    backgroundColor: '#e9ecef',
                  }}
                >
                  <div style={styles.messageContent}>
                    <span style={styles.messageName}>
                      {getQuestionDisplayName({
                        name: question.name,
                        isAnonymous: features.hasAnonymousQuestions,
                      })}
                      :{' '}
                    </span>
                    <span style={styles.messageText}>{question.text}</span>
                    <button
                      onClick={() => {
                        console.log('Toggling question:', question.id);
                        handleToggleVisibility(question.id, question.visible);
                      }}
                      style={styles.showButton}
                    >
                      Show
                    </button>
                  </div>
                  <div style={styles.messageTimestamp}>
                    {question.timestamp?.toDate().toLocaleTimeString()}
                  </div>
                </div>
              ))}
            <div ref={questionsEndRef} />
          </div>
        )}
      </div>
    </>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '500px',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  header: {
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    padding: '4px 5px',
    boxSizing: 'border-box',
  },
  questionsWindow: {
    flex: 1,
    padding: '5px',
    overflowY: 'auto',
    backgroundColor: '#f5f5f5',
  },
  inputArea: {
    padding: '4px 5px 1px 5px',
    backgroundColor: '#fff',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    boxSizing: 'border-box',
    borderRadius: '8px',
  },
  inputWrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  input: {
    flex: 1,
    padding: '3px 6px',
    borderRadius: '4px 0 0 4px',
    border: '1px solid #ccc',
    borderRight: 'none',
    resize: 'vertical',
    minHeight: '60px',
    maxHeight: '150px',
    boxSizing: 'border-box',
    width: '0',
    fontSize: '13px',
  },
  button: {
    padding: '8px',
    backgroundColor: '#0d6efd',
    color: '#fff',
    border: 'none',
    borderRadius: '0 4px 4px 0',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontSize: '13px',
  },
  message: {
    marginBottom: '4px',
    padding: '6px 10px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  messageContent: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  messageText: {
    fontSize: '13px',
  },
  messageTimestamp: {
    fontSize: '11px',
    color: '#666',
    marginTop: '2px',
  },
  messageName: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  hideButton: {
    marginLeft: 'auto',
    padding: '2px 6px',
    fontSize: '11px',
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  showButton: {
    marginLeft: 'auto',
    padding: '2px 6px',
    fontSize: '11px',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export default QuestionBox;
